import { useState } from 'react';
import { CreditCard, Lock } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatNumber } from 'features/usage/utils/usage-format';
import { useTopUpPayment } from '../hooks/use-top-up-payment';
import type { CouponQuote, CreditSummary } from '../types';
import { creditsFor, defaultPreset, presetsOrDefault, toMinor } from '../utils/amount';
import { cardLabel } from '../utils/cards';
import { AmountPicker } from './amount-picker';
import { CouponRedeem } from './coupon-redeem';
import { OpenCheckoutNotice } from './open-checkout-notice';
import { TopUpBillingDialog } from './topup-billing-dialog';
import { TopUpProgress } from './topup-progress';

const SummaryRow = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className="flex justify-between gap-4 py-2.5">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className={strong ? 'font-semibold tabular-nums' : 'font-medium tabular-nums'}>{value}</dd>
  </div>
);

/** Pick an amount and pay: on the saved card with no redirect, or on the hosted checkout page. */
export const TopUpForm = ({ summary }: { summary: CreditSummary }) => {
  const { t } = useTranslation();
  const presets = presetsOrDefault(summary.presetsChf);
  const [amountChf, setAmountChf] = useState<number | null>(defaultPreset(presets));
  const [coupon, setCoupon] = useState<{ code: string; quote: CouponQuote } | null>(null);
  const [couponCardKey, setCouponCardKey] = useState(0);
  const {
    card,
    isChecking,
    isPending,
    isChargingCard,
    isRedirecting,
    chargeId,
    clearCharge,
    hasFailed,
    failureText,
    start,
    pay,
    billing,
  } = useTopUpPayment(summary, amountChf, coupon?.code);

  const canPay = amountChf !== null && !isPending && !isRedirecting;
  const dueMinor = amountChf === null ? null : (coupon?.quote.payableMinor ?? toMinor(amountChf));
  const amountLabel = dueMinor === null ? '' : formatMinor(dueMinor, summary.currency);

  const applyCoupon = (code: string, quote: CouponQuote) => {
    setCoupon({ code, quote });
    if (quote.payableMinor === 0) start(undefined, code);
  };

  const payLabel = () => {
    if (!card) return t('CONTINUE_TO_CHECKOUT', 'Continue to checkout');
    return amountChf === null
      ? t('PAY_WITH_CARD_ONLY', { card: cardLabel(card, t('CARD')), defaultValue: 'Pay with {{card}}' })
      : t('PAY_WITH_CARD', {
          amount: amountLabel,
          card: cardLabel(card, t('CARD')), defaultValue: 'Pay {{amount}} with {{card}}',
        });
  };

  const hint = () => {
    if (isRedirecting) return t('OPENING_CHECKOUT', 'Opening checkout...');
    if (card) {
      return t('SAVED_CARD_HINT', 'Your saved card is charged right away.');
    }
    return t(
      'CHECKOUT_HINT',
      'You enter your card on the next page, where you can save it for next time.'
    );
  };

  return (
    <Card className="border-border/70 shadow-none">
      <CardContent className="space-y-4">
        {summary.openTopUp && <OpenCheckoutNotice openTopUp={summary.openTopUp} />}
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-foreground sm:text-base">
            {t('TOP_UP', 'Top up')}
          </h3>
          <p className="text-xs text-muted-foreground sm:text-sm">
            {t('TOPUP_RATE', {
              amount: formatMinor(100, summary.currency),
              credits: formatNumber(summary.creditsPerChf),
              defaultValue: '{{amount}} = {{credits}} credits. Credits never expire.',
            })}
          </p>
        </div>

        <fieldset
          disabled={isPending || isRedirecting || !!chargeId}
          className="min-w-0 border-0 p-0"
        >
          <AmountPicker
            presets={presets}
            minChf={summary.minTopUpChf}
            maxChf={summary.maxTopUpChf}
            initialChf={amountChf}
            creditsPerChf={summary.creditsPerChf}
            onChange={(amount) => {
              setAmountChf(amount);
              setCoupon(null);
            }}
          />
        </fieldset>

        <CouponRedeem
          key={couponCardKey}
          amountChf={amountChf}
          disabled={isPending || isRedirecting || !!chargeId}
          applied={coupon?.quote}
          onQuote={applyCoupon}
          onClear={() => setCoupon(null)}
        />

        <dl className="divide-y rounded-xl border bg-muted/40 px-4 text-sm" aria-live="polite">
          <SummaryRow
            label={t('CREDITS_YOU_GET', 'Credits you get')}
            value={
              amountChf === null
                ? '-'
                : formatNumber(coupon?.quote.credits ?? creditsFor(amountChf, summary.creditsPerChf))
            }
          />
          {coupon && (
            <SummaryRow
              label={t('COUPON_DISCOUNT', 'Coupon discount')}
              value={`-${formatMinor(coupon.quote.discountMinor, summary.currency)}`}
            />
          )}
          <SummaryRow
            label={t('DUE_NOW', 'Due now')}
            value={amountChf === null ? '-' : amountLabel}
            strong
          />
        </dl>

        {hasFailed && (
          <Alert variant="destructive">
            <AlertDescription>{failureText}</AlertDescription>
          </Alert>
        )}

        {chargeId && (
          <TopUpProgress
            topUpId={chargeId}
            onDone={() => {
              clearCharge();
              setCoupon(null);
              setCouponCardKey((key) => key + 1);
            }}
            onUseCheckout={() => {
              clearCharge();
              start();
            }}
          />
        )}

        {!chargeId && isChecking && <Skeleton className="h-9 w-full sm:w-64" />}

        {!chargeId && !isChecking && (
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <Button
                className="h-11 w-full px-6 sm:w-auto"
                disabled={!canPay}
                loading={card ? isChargingCard : isPending}
                onClick={() => pay(card?.paymentMethodId)}
              >
                {!isPending && <CreditCard aria-hidden="true" />}
                {payLabel()}
              </Button>
              {card && (
                <Button
                  variant="ghost"
                  className="w-full sm:w-auto"
                  disabled={!canPay}
                  loading={isPending && !isChargingCard}
                  onClick={() => pay()}
                >
                  {t('USE_DIFFERENT_CARD', 'Use a different card')}
                </Button>
              )}
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
              <Lock aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              {hint()}
            </p>
          </div>
        )}

        <TopUpBillingDialog
          open={billing.isOpen}
          amount={amountLabel}
          onClose={billing.close}
          onSaved={billing.onSaved}
        />
      </CardContent>
    </Card>
  );
};
